import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Select from "lib/common/controls/Select";
import Button from "lib/common/descriptorcontrols/BaseControls/Button";
import SearchForm from "./views/SearchForm";
import SearchResults from "./views/SearchResults";

import { backboneConnect } from "lib/common/hoc/BackboneReact/backboneConnect";
import { appAlert } from "lib/withOver";
import SearchFormStore from "data/SearchFormStore";
import { buildMenu } from "data/selectors/stereotypes";
import ErrorBoundary from "lib/common/components/ErrorBoundary";
import StereotypesStore from "data/StereotypesStore";
import SearchResultStore from "data/SearchResultStore";
import Dispatcher from "Dispatcher";
import SearchFormActions from "data/SearchFormActions";
import userStore from "data/userStore";
import SplitButton from "lib/common/components/SplitButton";
import useFluxStore from "@armus/use-flux-store";

const TheButton = props => {
    const { stereoKey, menuItemOnClick } = props;
    const {
        options,
        newTypeLabel,
    } = useFluxStore(() => {
        let stereo = StereotypesStore.getStereotypeByKey(stereoKey);
        if (stereo.magicRootKey != null) {
            stereo = StereotypesStore.getStereotypeByKey(stereo.magicRootKey);
        }
        return {
            options: buildMenu(stereo.key)
                .map(it =>
                    ({value: it.key, ...it})),
            newTypeLabel: stereo.name,
        };
    }, [
        StereotypesStore,
    ], [stereoKey]);
    if(options.length === 0) {
        return null; // nothing to create.
    }
    return (
        <Button
            id="newEntity"
            enabled={true}
            onClick={menuItemOnClick}
            className={"btn btn-create"}
            options={options}
            dataAlign={"fill"}
            ddActionName={`Create ${newTypeLabel}`}
            label={`Create a new ${newTypeLabel}`}
        />
    );
};

TheButton.propTypes = {
    stereoKey: PropTypes.string.isRequired,
    menuItemOnClick: PropTypes.func.isRequired,
};

export class SearchModule extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            resultMaxHeight: "30vh",
        };

        this.onSearch = this.onSearch.bind(this);
        this.onSearchAndSave = this.onSearchAndSave.bind(this);
        this.onRestoreAndSearch = this.onRestoreAndSearch.bind(this);

        this.formLayoutRef = React.createRef();
    }

    componentDidMount() {
        this.measure();
    }

    componentDidUpdate() {
        this.measure();
    }

    measure() {
        const {boundingElement} = this.props;
        if (!boundingElement) return;
        const boundingRect = boundingElement.getBoundingClientRect();
        const availableHeight = boundingRect.height;
        if (availableHeight === 0) return; // thanks marionette
        const fel = this.formLayoutRef.current;
        if (!fel) return;
        if (!document.body.contains(fel)) return; // thanks marionette
        const resultMaxHeight = Math.max(
            80, // always want this much
            availableHeight +
                boundingRect.top + // chrome
                -fel.getBoundingClientRect().bottom + // form
                -55 // pull
                -38 // pagination
            ) + "px";
        this.setState(s => {
            if (s.resultMaxHeight === resultMaxHeight) return;
            return {
                resultMaxHeight,
            };
        });
    }

    onSearch() {
        const {
            fluxContext,
            onSearch,
            showEmptySearchAlert,
        } = this.props;
        if (!SearchFormStore.hasValues(fluxContext)) {
            showEmptySearchAlert();
            return;
        }
        onSearch && onSearch();
    }

    onSearchAndSave() {
        const {
            fluxContext,
            onSearchAndSaveDefault,
            showEmptySearchAlert,
        } = this.props;
        if (!SearchFormStore.hasValues(fluxContext)) {
            showEmptySearchAlert();
            return;
        }
        onSearchAndSaveDefault && onSearchAndSaveDefault();
    }

    onRestoreAndSearch() {
        const {
            defaultSearch,
            onRestoreDefaultSearch,
            showNoSavedSearchAlert,
        } = this.props;
        if (!defaultSearch.enabled || !defaultSearch.query) {
            showNoSavedSearchAlert();
            return;
        }
        onRestoreDefaultSearch && onRestoreDefaultSearch();
    }

    render() {
        const {
            title = "Find or Create New Record",
            stereotypes,
            selectedStereotype,
            fluxContext,
            menuItemOnClick,
            onStereotypeSelect,
            records,
            selectedRecord,
            columns,
            sorts,
            onSort,
            onRecordSelection,
            showDisabled,
            defaultSearch,
            showAdvanced = false,
            showCreate = true,
            pagination = {},
            onPage,
            isLoading,
        } = this.props;

        const hasStereotypes = stereotypes.length > 1;
        const typesClasses = classNames({
            "col-md-3": hasStereotypes,
            "hidden": !hasStereotypes
        });
        const formClasses = classNames({
            "col-md-6": hasStereotypes,
            "col-md-9": !hasStereotypes
        });

        const {
            key: searchByValue,
            name: searchByName,
            supportsEnablement,
        } = selectedStereotype;
        const searchByOptions = stereotypes.map((item) => {
            const value = item.key;
            return {
                value: value,
                // dynamic label
                label: searchByValue === value ? "Search By " + item.name : item.name
            };
        });

        const useDefaultSearch = !!defaultSearch ? defaultSearch.enabled : false;
        const useStandardSearch = !useDefaultSearch;
        const hasDefaultSearch = useDefaultSearch ? defaultSearch.query : false;
        const missingDefaultSearch = !hasDefaultSearch;
        const searchButton = useStandardSearch
            ? <Button
                enabled={true}
                icon="search"
                onClick={this.onSearch}
                className="btn btn-primary search-button"
                label="Search"
                ddActionName={`Search ${searchByName}`}
            />
            : [
                <SplitButton
                    key="search-button"
                    type="primary"
                    className="search-button"
                    ddActionName={`Search ${searchByName}`}
                    actions={[
                        {
                            icon: "search",
                            label: "Search",
                            value: "search",
                            onSelect: this.onSearch,
                        }, {
                            label: "Search and Save as Default",
                            value: "save",
                            onSelect: this.onSearchAndSave,
                        }, {
                            label: "Restore Default Search",
                            value: "restore",
                            onSelect: this.onRestoreAndSearch,
                        },
                    ]}
                />,
                missingDefaultSearch && <span
                    key="no-saved-search"
                    className="no-saved-search"
                    style={{
                        fontSize: "80%",
                        opacity: 0.6,
                    }}
                >
                    <sup>*</sup>
                    No default search saved
                </span>
            ];

        return (
            <ErrorBoundary>
                <div className="search-form-layout" ref={this.formLayoutRef}>
                    <div className="row">
                        <div className="col-md-12">
                            <h2>{title}</h2>
                        </div>
                    </div>
                    <div className="row">
                        <div className={typesClasses} id="types">
                            {hasStereotypes &&
                                <Select
                                    id="stereotype"
                                    options={searchByOptions}
                                    onCommitChange={onStereotypeSelect}
                                    value={searchByValue}
                                    label="Search By"
                                />
                            }
                        </div>
                        <div className={formClasses} id="form">
                            {<SearchForm
                                fluxContext={fluxContext}
                                onSubmit={this.onSearch}
                                showAdvanced={showAdvanced}
                                supportsRelativeDates
                                onHeightChange={() => this.forceUpdate()}
                            />}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-7">
                            {searchButton}
                        </div>
                        {showCreate && <div className="col-md-offset-2 col-md-3" id="new-entity">
                            <div className="btn-group full">
                                <TheButton
                                    stereoKey={selectedStereotype.key}
                                    menuItemOnClick={menuItemOnClick}
                                />
                            </div>
                        </div>}
                    </div>
                </div>
                <div id="results">
                    {(records || isLoading) && <SearchResults
                        records={records}
                        selected={selectedRecord}
                        columns={columns}
                        sorts={sorts}
                        onSort={onSort}
                        hasStereotype={hasStereotypes}
                        onRecordSelection={onRecordSelection}
                        showDisabled={showDisabled}
                        supportsEnablement={supportsEnablement}
                        maxHeight={this.state.resultMaxHeight}
                        pagination={pagination}
                        onPage={onPage}
                        isLoading={isLoading}
                    />}
                </div>
            </ErrorBoundary>
        );
    }
}

SearchModule.propTypes = {
    boundingElement: PropTypes.instanceOf(Node),

    // search form props
    fluxContext: PropTypes.string.isRequired,
    title:  PropTypes.string,
    stereotypes: PropTypes.array.isRequired,
    defaultSearch: PropTypes.shape({
        enabled: PropTypes.bool.isRequired,
        query: PropTypes.object,
    }),
    selectedStereotype: PropTypes.object.isRequired,
    onSearch: PropTypes.func.isRequired,
    onSearchAndSaveDefault: PropTypes.func,
    onRestoreDefaultSearch: PropTypes.func,
    showEmptySearchAlert: PropTypes.func.isRequired,
    showNoSavedSearchAlert: PropTypes.func,
    menuItemOnClick: PropTypes.func.isRequired,
    onStereotypeSelect: PropTypes.func.isRequired,
    showCreate: PropTypes.bool,
    showAdvanced: PropTypes.bool,

    // search result props
    records: PropTypes.array,
    selectedRecord: PropTypes.object,
    columns: PropTypes.array,
    sorts: PropTypes.object,
    onSort: PropTypes.func.isRequired,
    onRecordSelection: PropTypes.func.isRequired,
    showDisabled: PropTypes.bool,
    pagination: PropTypes.shape({
        page: PropTypes.number,
        pageCount: PropTypes.number,
        pageSize: PropTypes.number,
        totalResultCount: PropTypes.number
    }).isRequired,
    onPage: PropTypes.func,
    isLoading: PropTypes.bool,
};

const config = props => ({
    boundingElement: {
        listenOn: "appModel",
    },
});

export const BackboneSearchModule = backboneConnect(config)(props => {
    const state = useFluxStore(() => {
        const fluxContext = props.appModel.get("fluxContext");
        const searchKey = SearchFormStore.getKey(fluxContext);
        const searchResults = SearchResultStore.getMostRecentSearch(searchKey);
        const defaultSearch = props.useDefaultSearch
            ? userStore.getDefaultSearchConfig()
            : {enabled: false};
        return {
            fluxContext,
            stereotypes: StereotypesStore.getStereotypesByKeys(
                SearchFormStore.getAvailableKeys(fluxContext)),
            defaultSearch,
            selectedStereotype: StereotypesStore.getStereotypeByKey(searchKey),
            isLoading: SearchResultStore.isMostRecentSearchLoading(searchKey),
            records: searchResults && searchResults.results,
            pagination: {
                // Currently some paging values can not be trusted from the
                // results. Instead we are using the SearchFormStore's values
                // until there is a need to fix the values from the server.
                page: SearchFormStore.getPage(fluxContext),
                pageCount: searchResults && searchResults.pageCount, // cant be trusted
                pageSize: SearchFormStore.getPageSize(fluxContext),
                totalResultCount: searchResults && searchResults.totalResultCount, // cant be trusted.
            },
            columns: SearchFormStore.getFields(fluxContext),
            sorts: SearchFormStore.getSorts(fluxContext),
            onSort: column => {
                Dispatcher.contextDispatch(fluxContext, {
                    type: SearchFormActions.SORT,
                    key: column.key,
                });
            },
            onPage: page => {
                Dispatcher.contextDispatch(fluxContext, {
                    type: SearchFormActions.SELECT_PAGE,
                    page,
                });
            },
            selectedRecord: SearchFormStore.getSelectedResult(fluxContext),
            onRecordSelection: (result) => {
                Dispatcher.contextDispatch(fluxContext, {
                    type: SearchFormActions.SELECT_RESULT,
                    result,
                });
                Backbone.dispatcher.trigger("entity:load", result, props.appModel);
            },
            menuItemOnClick: (e, d, creatable) =>
                Backbone.dispatcher.trigger(
                    "entity:new",
                    creatable.key,
                    creatable.org,
                    creatable.entity,
                    props.appModel,
                ),
            onSearch: () =>
                Dispatcher.contextDispatch(fluxContext, {
                    type: SearchFormActions.INITIATE_SEARCH,
                    key: searchKey,
                }),
            onSearchAndSaveDefault: () =>
                Dispatcher.contextDispatch(fluxContext, {
                    type: SearchFormActions.INITIATE_SEARCH_AND_SAVE_DEFAULT,
                    key: searchKey,
                }),
            onRestoreDefaultSearch: () =>
                Dispatcher.contextDispatch(fluxContext, {
                    type: SearchFormActions.RESTORE_DEFAULT_AND_INITIATE_SEARCH,
                    key: defaultSearch.query.key,
                }),
            showEmptySearchAlert: () =>
                appAlert("Search requires at least one criterion."),
            showNoSavedSearchAlert: () =>
                appAlert("No default search is available to restore."),
            onStereotypeSelect: key =>
                Dispatcher.contextDispatch(fluxContext, {
                    type: SearchFormActions.SELECT_STEREOTYPE,
                    key,
                }),
        };
    }, [
        SearchFormStore,
        SearchResultStore,
        userStore,
    ], [
        props.appModel,
    ]);
    return <SearchModule {...props} {...state} />;
});



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/search/SearchModule.js